import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import {
  RouterProvider,
  createRouter,
  createRootRoute,
  createRoute,
  Outlet,
} from "@tanstack/react-router";
import "./app/globals.css";

// ---------------------------------------------------------------------------
// Lazy-loaded page components
// ---------------------------------------------------------------------------

const Providers = React.lazy(() =>
  import("@/components/layout/providers").then((m) => ({
    default: m.Providers,
  })),
);

const HomePageClient = React.lazy(() =>
  import("@/components/layout/home-page-client").then((m) => ({
    default: m.HomePageClient,
  })),
);

const ClaimLandingContainer = React.lazy(() =>
  import("@/components/containers/community/claim-landing-container").then(
    (m) => ({ default: m.ClaimLandingContainer || m.default }),
  ),
);

const SpaceDetailClient = React.lazy(() =>
  import("@/components/content/spaces/space-detail-client").then((m) => ({
    default: m.SpaceDetailClient || m.default,
  })),
);

// ---------------------------------------------------------------------------
// Route Definitions
// ---------------------------------------------------------------------------

// Root route — app shell with all providers
const rootRoute = createRootRoute({
  component: () => (
    <Suspense fallback={null}>
      <Providers>
        <Outlet />
      </Providers>
    </Suspense>
  ),
});

// Home route — the main SPA (query-param-driven)
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: () => (
    <Suspense fallback={null}>
      <HomePageClient />
    </Suspense>
  ),
});

// Community handle route: /c/:handle
const communityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/c/$handle",
  component: () => (
    <Suspense fallback={null}>
      <ClaimLandingContainer />
    </Suspense>
  ),
});

// Spaces route: /at/:slug
const spacesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/at/$slug",
  component: () => (
    <Suspense fallback={null}>
      <SpaceDetailClient />
    </Suspense>
  ),
});

// All other routes redirect to home with appropriate query params.
// The HomePageClient handles all routing via query params (?screen=X&panel=Y).
// Legacy paths like /auth, /u/:handle, /adm are mapped by url-params.ts normalizeAppHref.

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

const routeTree = rootRoute.addChildren([
  indexRoute,
  communityRoute,
  spacesRoute,
]);

const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

// ---------------------------------------------------------------------------
// Mount
// ---------------------------------------------------------------------------

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>,
);
